<template>
  <ExpandableCard
    v-model:expanded="isExpanded"
    class="location-card"
    :class="{
      pickup: isPickup,
      dropoff: !isPickup,
      'in-progress': internalValue.isInProgress,
      complete: isStopResolved,
      'showing-edit-button': showEditButton,
    }"
  >
    <div
      class="top-row flex"
      :class="{ 'showing-edit-button': showEditButton }"
    >
      <div class="cell-group">
        <KeyValuePair
          :label="
            stopLabel ??
            t('common.noParam', { param: t('locations.locations') })
          "
          :value="`${stopTypeDisplay} ${pickDropSequenceNumber ?? ''}`"
          class="bg-gray-lighter top-row-cell location-name-cell"
        />

        <div
          class="text-gray-darkest top-row-cell address-cell"
          :class="{ editable: editable }"
          @click="isLocationEditModalVisible = editable"
        >
          <NamedAddress
            :name="internalValue.location.locationName"
            :address="internalValue.location.toAddress()"
          />
          <FontAwesomeIcon
            v-if="editable"
            aria-hidden="true"
            class="edit-icon"
            :icon="['fas', 'pencil']"
          />
        </div>
      </div>

      <LineItemSummary
        class="top-row-cell"
        :line-items="lineItems"
        :label="`${stopTypeDisplay} ${pickDropSequenceNumber ?? ''}`"
        :line-item-link-route="
          firstLineItem?.domId
            ? { hash: `#${firstLineItem?.domId}`, query: queryParams }
            : undefined
        "
        uppercase-label
      />

      <div class="top-row-cell primary-appt">
        <PopupDateRange
          v-if="primaryAppointmentRange"
          v-model:start="internalValue[primaryAppointmentRange.start.field]"
          v-model:end="internalValue[primaryAppointmentRange.end.field]"
          v-model:time-zone="internalValue.location.timeZone"
          :label="primaryAppointmentRange.label"
          :start-label="primaryAppointmentRange.startLabel"
          :end-label="primaryAppointmentRange.endLabel"
          :hours-of-operation="hoursOfOperation"
          :disabled="isPrimaryAppointmentDisabled"
          :autofill="
            primaryAppointment.field !== 'actualArrival' &&
            primaryAppointment.field !== 'actualDeparture'
          "
        >
          <template #placeholder="{ label, placeholderStart, placeholderEnd }">
            <LocationCardDateRange
              :label="label"
              :start="placeholderStart"
              :end="placeholderEnd"
              uppercase-label
            >
              <template #label>
                <div class="label-with-icon">
                  {{ label }}
                  <InfoIcon
                    v-if="
                      internalValue.shippingPointDetails?.appointmentRequired
                    "
                    icon="pi pi-calendar-clock"
                    :tooltip="t('shippingPoints.appointmentRequired')"
                  />
                  <InfoIcon
                    v-if="
                      AppointmentType.REQUESTED ===
                        primaryAppointmentRange.type &&
                      (internalValue.requestedAppointmentDateLock ||
                        internalValue.requestedAppointmentTimeLock)
                    "
                    icon="pi pi-lock"
                    :tooltip="requestedAppointmentLockTooltip"
                  />
                </div>
              </template>
            </LocationCardDateRange>
          </template>

          <template #additionalFields>
            <div class="flex flex-gap-1">
              <WrappedSwitch
                v-if="internalValue.shippingPointDetails"
                :id="`${id}-primary-appointment-required`"
                v-model="internalValue.shippingPointDetails.appointmentRequired"
                :label="t('shippingPoints.appointmentRequired')"
                :disabled="isAppointmentRequiredDisabled"
              >
                <template #label="{ inputId, label }">
                  <div class="label-with-icon">
                    <label :for="inputId">
                      {{ label }}
                    </label>
                    <InfoIcon
                      v-if="isAppointmentRequiredDisabled"
                      :tooltip="
                        t('shippingPoints.appointmentRequiredLockedTooltip')
                      "
                    />
                  </div>
                </template>
              </WrappedSwitch>

              <template v-if="isPrimaryRequestedAppointment">
                <WrappedSwitch
                  :id="`${id}-primary-date-lock`"
                  v-model="internalValue.requestedAppointmentDateLock"
                  :label="t('shippingPoints.requestedDateLock')"
                />
                <WrappedSwitch
                  :id="`${id}-primary-time-lock`"
                  v-model="internalValue.requestedAppointmentTimeLock"
                  :label="t('shippingPoints.requestedTimeLock')"
                />
              </template>
            </div>
          </template>
        </PopupDateRange>
        <PopupCalendar
          v-else
          v-model="internalValue[primaryAppointment.field]"
          v-model:time-zone="internalValue.location.timeZone"
          :label="primaryAppointment.label"
          :validation-rules="primaryAppointment.validationRules"
          :disabled="!editable"
        >
          <template #placeholder="{ label, placeholder }">
            <LocationCardDate
              :label="label"
              :date="placeholder"
              uppercase-label
            >
              <template #label>
                <div class="label-with-icon">
                  {{ label }}
                  <InfoIcon
                    v-if="
                      internalValue.shippingPointDetails?.appointmentRequired
                    "
                    icon="pi pi-calendar-clock"
                    :tooltip="t('shippingPoints.appointmentRequired')"
                  />
                </div>
              </template>
            </LocationCardDate>
          </template>

          <template #additionalFields>
            <div class="flex flex-gap-1">
              <WrappedSwitch
                v-if="internalValue.shippingPointDetails"
                :id="`${id}-primary-date-appointment-required`"
                v-model="internalValue.shippingPointDetails.appointmentRequired"
                :label="t('shippingPoints.appointmentRequired')"
                :disabled="isAppointmentRequiredDisabled"
              >
                <template #label="{ inputId, label }">
                  <div class="label-with-icon">
                    <label :for="inputId">
                      {{ label }}
                    </label>
                    <InfoIcon
                      v-if="isAppointmentRequiredDisabled"
                      :tooltip="
                        t('shippingPoints.appointmentRequiredLockedTooltip')
                      "
                    />
                  </div>
                </template>
              </WrappedSwitch>
            </div>
          </template>
        </PopupCalendar>
      </div>

      <div v-if="showEditButton" class="top-row-cell edit-button-cell">
        <RygenButton
          mode="tertiary"
          :aria-label="
            t('common.editParam', { param: internalValue.stopLabel })
          "
          icon="pi pi-pencil"
          class="edit-button"
          @click="emit('edit', internalValue)"
        />
      </div>
    </div>

    <template #expansion>
      <div class="expansion-left border-right-1 border-color-divider">
        <div class="location-card-appointments">
          <h3 class="font-semibold section-subheader mt-0">
            {{
              t('common.paramTimes', {
                param: isPickup
                  ? t('shipments.pickups')
                  : t('shipments.dropoffs'),
                count: 2,
              })
            }}
          </h3>
          <div class="location-card-appointments-content">
            <div class="location-card-appointment-row">
              <div class="appointment-cell">
                <PopupDateRange
                  v-model:start="internalValue.requestedAppointmentEarliest"
                  v-model:end="internalValue.requestedAppointmentLatest"
                  v-model:time-zone="internalValue.location.timeZone"
                  :label="
                    isPickup
                      ? t('shipments.dates.requestedPickup')
                      : t('shipments.dates.requestedDropoff')
                  "
                  :start-label="t('shipments.dates.earliest')"
                  :end-label="t('shipments.dates.latest')"
                  :hours-of-operation="hoursOfOperation"
                  :disabled="!editable"
                  autofill
                >
                  <template
                    #placeholder="{ label, placeholderStart, placeholderEnd }"
                  >
                    <LocationCardDateRange
                      :start="placeholderStart"
                      :end="placeholderEnd"
                    >
                      <template #label>
                        <div class="label-with-icon">
                          {{ label }}
                          <InfoIcon
                            v-if="
                              internalValue.shippingPointDetails
                                ?.appointmentRequired
                            "
                            icon="pi pi-calendar-clock"
                            :tooltip="t('shippingPoints.appointmentRequired')"
                          />
                          <InfoIcon
                            v-if="shouldDisplayRequestedAppointmentLock"
                            icon="pi pi-lock"
                            :tooltip="requestedAppointmentLockTooltip"
                          />
                        </div>
                      </template>
                    </LocationCardDateRange>
                  </template>

                  <template #additionalFields>
                    <div class="flex flex-gap-1">
                      <WrappedSwitch
                        v-if="internalValue.shippingPointDetails"
                        :id="`${id}-requested-appointment-required`"
                        v-model="
                          internalValue.shippingPointDetails.appointmentRequired
                        "
                        :label="t('shippingPoints.appointmentRequired')"
                        :disabled="isAppointmentRequiredDisabled"
                      >
                        <template #label="{ inputId, label }">
                          <div class="label-with-icon">
                            <label :for="inputId">
                              {{ label }}
                            </label>
                            <InfoIcon
                              v-if="isAppointmentRequiredDisabled"
                              :tooltip="
                                t(
                                  'shippingPoints.appointmentRequiredLockedTooltip',
                                )
                              "
                            />
                          </div>
                        </template>
                      </WrappedSwitch>
                      <WrappedSwitch
                        :id="`${id}-requested-date-lock`"
                        v-model="internalValue.requestedAppointmentDateLock"
                        :label="t('shippingPoints.requestedDateLock')"
                      />
                      <WrappedSwitch
                        :id="`${id}-requested-time-lock`"
                        v-model="internalValue.requestedAppointmentTimeLock"
                        :label="t('shippingPoints.requestedTimeLock')"
                      />
                    </div>
                  </template>
                </PopupDateRange>
              </div>

              <div class="appointment-cell">
                <PopupDateRange
                  v-if="isShipmentStop"
                  v-model:start="internalValue.scheduledAppointmentEarliest"
                  v-model:end="internalValue.scheduledAppointmentLatest"
                  v-model:time-zone="internalValue.location.timeZone"
                  :label="
                    isPickup
                      ? t('shipments.dates.scheduledPickup')
                      : t('shipments.dates.scheduledDropoff')
                  "
                  :start-label="t('shipments.dates.earliest')"
                  :end-label="t('shipments.dates.latest')"
                  :hours-of-operation="hoursOfOperation"
                  :disabled="!editable"
                  autofill
                >
                  <template
                    #placeholder="{ label, placeholderStart, placeholderEnd }"
                  >
                    <LocationCardDateRange
                      :label="label"
                      :start="placeholderStart"
                      :end="placeholderEnd"
                    />
                  </template>
                </PopupDateRange>
              </div>
            </div>

            <div class="location-card-appointment-row">
              <div class="appointment-cell">
                <PopupCalendar
                  v-if="isShipmentStop"
                  v-model="internalValue.expectedArrival"
                  v-model:time-zone="internalValue.location.timeZone"
                  :label="expectedArrivalLabel"
                  :validation-rules="expectedArrivalRules"
                  :disabled="!editable"
                >
                  <template #placeholder="{ label, placeholder }">
                    <LocationCardDate
                      :label="label"
                      :date="placeholder"
                      use-placeholder
                    />
                  </template>
                </PopupCalendar>
              </div>

              <div v-if="isShipmentStop" class="appointment-cell">
                <PopupDateRange
                  v-model:start="internalValue.actualArrival"
                  v-model:end="internalValue.actualDeparture"
                  v-model:time-zone="internalValue.location.timeZone"
                  :label="
                    isPickup
                      ? t('shipments.dates.actualPickup')
                      : t('shipments.dates.actualDropoff')
                  "
                  :start-label="t('shipments.dates.arrival')"
                  :end-label="t('shipments.dates.departure')"
                  :disabled="!isActualDatesEditable"
                >
                  <template #placeholder="{ label }">
                    <ActualDateView
                      :arrival="internalValue.actualArrival"
                      :departure="internalValue.actualDeparture"
                      :label="label"
                    />
                  </template>
                </PopupDateRange>
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="dockAppointment.data?.id"
          class="border-top-1 border-color-divider"
        >
          <div class="location-card-appointments">
            <div class="location-card-appointments-header">
              <h3 class="font-semibold section-subheader mt-0">
                {{ t('dockScheduling.dockScheduling') }}
              </h3>

              <DockAppointmentStatusChip :dock-appointment="dockAppointment" />

              <img :src="conduitLogo" class="conduit-logo" />
            </div>

            <div class="location-card-appointments-content">
              <div class="location-card-appointment-row">
                <div class="appointment-cell">
                  <LocationCardDate
                    :label="
                      t('shipments.dates.paramAppointment', {
                        param: stopTypeLabel,
                      })
                    "
                    :date="dockAppointment.data?.scheduledEarliest"
                    use-placeholder
                  />
                </div>

                <div class="appointment-cell">
                  <ActualDateView
                    :arrival="dockAppointment.data?.actualArrive"
                    :departure="dockAppointment.data?.actualDepart"
                    :label="
                      t('shipments.dates.actualParam', {
                        param: stopTypeLabel,
                      })
                    "
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="accessorials">
          <ul
            v-if="internalValue.requestedAccessorials.length"
            class="flex flex-gap-1 flex-wrap list-none p-0 m-0"
            role="list"
          >
            <li
              v-for="(accessorial, i) in internalValue.requestedAccessorials"
              :key="accessorial.id"
            >
              <AccessorialInlineEdit
                :model-value="internalValue.requestedAccessorials[i]"
                :accessorial-codes="accessorialCodes.data"
                :editable="editable"
                @remove="onRemoveAccessorial(i)"
                @update:model-value="onUpdateAccessorial(i, $event)"
              />
            </li>
          </ul>

          <InputWrapper
            v-if="isAddingAccessorial"
            v-slot="{ ariaProps, inputId }"
            :input-id="`${id}-new-code`"
            :label="t('referenceData.accessorials')"
            class="field w-6"
          >
            <RygenDropdown
              :id="inputId"
              v-model="newAccessorial"
              v-bind="ariaProps"
              :options="filteredAccessorialCodes"
              option-label="name"
              data-key="naturalId"
              filter
              :placeholder="t('referenceData.accessorials')"
            />
          </InputWrapper>
          <div v-else-if="!internalValue.requestedAccessorials.length">
            {{
              t('common.noParam', {
                param: t('referenceData.accessorials', 2),
              })
            }}
          </div>

          <div class="accessorial-buttons flex flex-gap-1">
            <RygenButton
              v-if="isAddingAccessorial"
              mode="tertiary"
              :label="t('common.remove')"
              icon="pi pi-times"
              icon-pos="left"
              @click="onCancelAccessorial"
            />
            <RygenButton
              v-if="isAddingAccessorial"
              mode="tertiary"
              :disabled="!newAccessorial?.id"
              :label="t('common.save')"
              icon="pi pi-check"
              icon-pos="left"
              @click="onSubmitAccessorial"
            />
            <AddButton
              v-else-if="editable"
              :label="t('referenceData.accessorials')"
              @click="onAddAccessorial"
            />
          </div>
        </div>
      </div>

      <LocationCardDetail
        :id="`${id}-detail`"
        v-model:contact="internalContact"
        class="expansion-right"
        :hours="internalValue.shippingPointDetails?.hoursOfOperation"
        :location-group-ids="internalValue.location.locationGroupIds"
        :editable="editable"
      />
    </template>

    <template
      v-if="internalValue.shippingPointDetails && !hideNotes"
      #secondaryContent
    >
      <div class="flex m-0 bg-gray-lighter">
        <PopupTextArea
          :id="`${id}-carrier-special-instructions`"
          v-model="
            internalValue.shippingPointDetails.carrierSpecialInstructions
          "
          :class="{
            'w-6 border-right-1 border-color-divider': isAuthenticatedUser,
          }"
          :label="t('shippingPoints.carrierSpecialInstructions')"
          :disabled="!editable"
        />
        <PopupTextArea
          v-if="isAuthenticatedUser"
          :id="`${id}-internal-notes`"
          v-model="internalValue.shippingPointDetails.internalNotes"
          class="w-6"
          :label="t('orders.paramNotes', { param: t('orders.internal') })"
          :disabled="!editable"
        />
      </div>
    </template>

    <LocationEditModal
      :id="`${id}-location-edit-modal`"
      v-model:visible="isLocationEditModalVisible"
      :header="headerText"
      :location="internalValue"
      :editable="editable"
      @save="onUpdateLocation"
    />
  </ExpandableCard>
</template>

<script lang="ts" setup>
import axios from 'axios';
import isEqual from 'lodash/isEqual';
import type { PropType, Ref } from 'vue';
import { computed, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import { useToast, validDateTime } from '@rygen/rygen-ui/composables';
import { translateEnum } from '@rygen/rygen-ui/i18n';
import { AccessorialCode, Loadable, Location } from '@rygen/rygen-ui/model';

import api from '@/api';
import conduitLogo from '@/assets/img/conduit_logo.png';
import { AppointmentType, useShippingAppointments } from '@/composables';
import {
  DockAppointment,
  EmbeddableContact,
  LineItem,
  OrderShippingPoint,
  Shipment,
  ShipmentStatus,
  ShipmentStop,
  ShipmentStopType,
  ShippingPoint,
} from '@/model';
import { useLocationGroupStore } from '@/stores/LocationGroup';
import { ParentEntity } from '@/stores/ParentEntity';
import { isStatusEqualOrBefore } from '@/util';

import AccessorialInlineEdit from '@/components/accessorial/AccessorialInlineEdit.vue';
import DockAppointmentStatusChip from '@/components/dockappointment/DockAppointmentStatusChip.vue';
import LineItemSummary from '@/components/lineItem/LineItemSummary.vue';
import ActualDateView from '@/components/location/ActualDateView.vue';
import LocationCardDate from '@/components/location/LocationCardDate.vue';
import LocationCardDateRange from '@/components/location/LocationCardDateRange.vue';
import LocationCardDetail from '@/components/location/LocationCardDetail.vue';
import LocationEditModal, {
  LocationEditPayload,
} from '@/components/location/LocationEditModal.vue';
import AddButton from '@/components/ui/AddButton.vue';
import NamedAddress from '@rygen/rygen-ui/components/address/NamedAddress.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import RygenDropdown from '@rygen/rygen-ui/components/form/RygenDropdown.vue';
import WrappedSwitch from '@rygen/rygen-ui/components/form/WrappedSwitch.vue';
import PopupCalendar from '@rygen/rygen-ui/components/popup/PopupCalendar.vue';
import PopupDateRange from '@rygen/rygen-ui/components/popup/PopupDateRange.vue';
import PopupTextArea from '@rygen/rygen-ui/components/popup/PopupTextarea.vue';
import ExpandableCard from '@rygen/rygen-ui/components/ui/ExpandableCard.vue';
import InfoIcon from '@rygen/rygen-ui/components/ui/InfoIcon.vue';
import KeyValuePair from '@rygen/rygen-ui/components/ui/KeyValuePair.vue';

const internalValue = defineModel('location', {
  type: Object as PropType<ShippingPoint>,
  default: () => new OrderShippingPoint(),
});

const isExpanded = defineModel('expanded', {
  type: Boolean,
  default: () => false,
});

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  parentEntity: {
    type: Object as PropType<ParentEntity>,
    default: () => undefined,
  },
  // TODO: Replace this with a `StopSelector`. or do we even need it at all? it could be calculated in here instead of passed down
  pickDropType: {
    type: String as PropType<'origin' | 'pickup' | 'dropoff' | 'destination'>,
    default: () => 'pickup',
  },
  lineItems: {
    type: Array as PropType<LineItem[]>,
    default: () => [],
  },
  pickDropSequenceNumber: {
    type: Number,
    default: () => undefined,
  },
  accessorialCodes: {
    type: Object as PropType<Loadable<AccessorialCode[]>>,
    default: () => ({ loading: false }),
  },
  requireBothDates: {
    type: Boolean,
    default: () => false,
  },
  editable: {
    type: Boolean,
    default: () => false,
  },
  hideNotes: {
    type: Boolean,
    default: () => false,
  },
  showEditButton: {
    type: Boolean,
    default: () => false,
  },
  isAuthenticatedUser: {
    type: Boolean,
    default: () => false,
  },
});
const {
  id,
  parentEntity,
  pickDropType,
  lineItems,
  pickDropSequenceNumber,
  accessorialCodes,
  requireBothDates,
  editable,
  hideNotes,
  showEditButton,
  isAuthenticatedUser,
} = toRefs(props);
const emit = defineEmits<{
  (event: 'change', isChanged: boolean): void;
  (event: 'edit', stop: ShippingPoint): void;
  (event: 'update:location', newVal: ShippingPoint): void;
}>();

const { t } = useI18n();
const router = useRouter();
const { primaryAppointment, primaryAppointmentRange } =
  useShippingAppointments(internalValue);
const toast = useToast();
const { resolveLocationGroups } = useLocationGroupStore();

const Clazz = Object.getPrototypeOf(internalValue.value).constructor;
const originalState: ShippingPoint = new Clazz(internalValue.value);

const isLocationEditModalVisible = ref(false);
const newAccessorial: Ref<AccessorialCode | undefined> = ref();
const isAddingAccessorial = ref(false);
const dockAppointment: Ref<Loadable<DockAppointment>> = ref({ loading: false });

const queryParams = computed(() => router.currentRoute.value.query);

const shipment = computed(() => {
  return parentEntity?.value instanceof Shipment
    ? parentEntity.value
    : undefined;
});

const isShipmentStop = computed(() => {
  return ShipmentStop.isShipmentStop(internalValue.value);
});

const isStopResolved = computed(() => {
  return internalValue.value.isResolved(requireBothDates.value);
});

const headerText = computed(() => {
  switch (pickDropType.value) {
    case 'origin':
      return t('orders.origin');
    case 'pickup':
      return t('shipments.pickups');
    case 'dropoff':
      return t('shipments.dropoffs');
    case 'destination':
      return t('orders.destination');
    default:
      console.warn('Bad pickDropType', pickDropType.value);
      return t('orders.origin');
  }
});

const filteredAccessorialCodes = computed(() => {
  return (
    accessorialCodes.value.data?.filter(
      (code) =>
        !internalValue.value.requestedAccessorials.some(
          (item) => item.id === code.id,
        ),
    ) ?? []
  );
});

const internalContact: Ref<EmbeddableContact> = computed({
  get: () => internalValue.value.contact ?? new EmbeddableContact(),
  set: (newVal?: EmbeddableContact) => {
    internalValue.value.contact = new EmbeddableContact(newVal);
  },
});

const firstLineItem: Ref<LineItem | undefined> = computed(
  () => lineItems.value[0],
);

const stopLabel = computed(() => {
  return internalValue.value.location.cityState;
});

const isPickup = computed(() => {
  return internalValue.value.stopType === ShipmentStopType.PICKUP;
});

const stopTypeDisplay = computed(() => {
  return isPickup.value ? t('shipments.picks') : t('shipments.drops');
});

const stopTypeLabel = computed(() => {
  return translateEnum(internalValue.value.stopType, 'ShipmentStopType');
});

const expectedArrivalLabel = computed(() => {
  return isPickup.value
    ? t('shipments.dates.expectedPickup')
    : t('shipments.dates.expectedDropoff');
});

const expectedArrivalRules = computed(() => ({
  validDate: validDateTime(expectedArrivalLabel.value),
}));

const hoursOfOperation = computed(() => {
  return internalValue.value.shippingPointDetails?.hoursOfOperation;
});

const isActualDatesEditable = computed(() => {
  if (!shipment.value) {
    return editable.value;
  }

  // Actual dates should only be editable when shipment is booked or later
  // (not in PLANNING, CARRIER_ASSIGNED, TENDERED, or TENDER_REJECTED)
  const allowedStatuses = [
    ShipmentStatus.BOOKED,
    ShipmentStatus.IN_TRANSIT,
    ShipmentStatus.DELIVERED,
  ];
  return (
    editable.value &&
    shipment.value.status !== undefined &&
    allowedStatuses.includes(shipment.value.status)
  );
});

const isPrimaryAppointmentDisabled = computed(() => {
  // If the primary appointment is showing actual dates, use the status-based restriction
  const isPrimaryActualDate =
    primaryAppointment.value.field === 'actualArrival' ||
    primaryAppointment.value.field === 'actualDeparture';

  return isPrimaryActualDate ? !isActualDatesEditable.value : !editable.value;
});

const requestedAppointmentLockTooltip = computed(() => {
  let datesAndTimesText = '';
  if (
    internalValue.value.requestedAppointmentDateLock &&
    internalValue.value.requestedAppointmentTimeLock
  ) {
    datesAndTimesText = t('common.andParam', {
      before: t('common.dates', 2),
      after: t('common.times', 2),
    });
  } else if (internalValue.value.requestedAppointmentDateLock) {
    datesAndTimesText = t('common.dates', 2);
  } else if (internalValue.value.requestedAppointmentTimeLock) {
    datesAndTimesText = t('common.times', 2);
  }

  return t('shippingPoints.requestedLockTooltip', {
    datesAndTimes: datesAndTimesText,
  });
});

const shouldDisplayRequestedAppointmentLock = computed(
  () =>
    internalValue.value.requestedAppointmentDateLock ||
    internalValue.value.requestedAppointmentTimeLock,
);

const isAppointmentRequiredDisabled = computed(() => {
  if (!editable.value) {
    return true;
  }

  if (!shipment.value) {
    return false;
  }

  return !isStatusEqualOrBefore(shipment.value.status, ShipmentStatus.BOOKED);
});

const isPrimaryRequestedAppointment = computed(
  () =>
    primaryAppointmentRange.value &&
    AppointmentType.REQUESTED === primaryAppointmentRange.value.type,
);

const onAddAccessorial = () => {
  newAccessorial.value = undefined;
  isAddingAccessorial.value = true;
};

const onSubmitAccessorial = () => {
  if (newAccessorial.value?.id) {
    internalValue.value.requestedAccessorials.push(newAccessorial.value);
    newAccessorial.value = undefined;
    isAddingAccessorial.value = false;
  }
};

const onCancelAccessorial = () => {
  newAccessorial.value = undefined;
  isAddingAccessorial.value = false;
};

const onRemoveAccessorial = (index: number) => {
  internalValue.value.requestedAccessorials = [
    ...internalValue.value.requestedAccessorials.slice(0, index),
    ...internalValue.value.requestedAccessorials.slice(index + 1),
  ];
  emit('update:location', internalValue.value);
  emit('change', true);
};

const onUpdateAccessorial = (i: number, accessorial: AccessorialCode) => {
  internalValue.value.requestedAccessorials[i] = accessorial;
};

const loadDockAppointment = async (shipmentStopId: number) => {
  try {
    dockAppointment.value.loading = true;
    dockAppointment.value.data =
      await api.dockAppointment.getAppointmentForStop(shipmentStopId);
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error loading dock scheduling info', e);
    toast.addError({
      detail: t('errors.loadingParam', {
        param: t('dockScheduling.dockScheduling'),
      }),
      error: e,
    });
  } finally {
    dockAppointment.value.loading = false;
  }
};

const sendUpdateToFicon = async (shippingPoint: ShippingPoint) => {
  try {
    const groups = resolveLocationGroups(
      shippingPoint.location.locationGroupIds,
    );
    const location = new Location({
      id: shippingPoint.location.locationId,
      name: shippingPoint.location.locationName,
      address: shippingPoint.location.toAddress(),
      timeZone: shippingPoint.location.timeZone,
      groups,
    });
    await api.shippingPoint.update(location);
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error updating location in Ficon', e);
    toast.addError({
      detail: t('locations.errors.updatingAddressBook'),
      error: e,
    });
  }
};

const onUpdateLocation = ({
  location: updatedLocation,
  updateAddressBook,
}: LocationEditPayload) => {
  //have to do it like this because for some reason doing internalValue.value = updatedLocation does not trigger the watch for OrderDetail
  internalValue.value.location = updatedLocation.location;
  internalValue.value.requestedAccessorials =
    updatedLocation.requestedAccessorials;
  internalContact.value = new EmbeddableContact(updatedLocation.contact);

  if (internalValue.value.shippingPointDetails) {
    internalValue.value.shippingPointDetails.carrierSpecialInstructions =
      updatedLocation.shippingPointDetails?.carrierSpecialInstructions;
    internalValue.value.shippingPointDetails.internalNotes =
      updatedLocation.shippingPointDetails?.internalNotes;
  }

  const isDirty = !isEqual(updatedLocation.location, originalState.location);
  if (updateAddressBook && isDirty) {
    sendUpdateToFicon(updatedLocation);
  }
};

watch(
  internalValue,
  (newVal: ShippingPoint) => {
    if (!isEqual(newVal, originalState)) {
      emit('update:location', newVal);
      emit('change', true);
    }
  },
  { deep: true },
);

watch(
  () => internalValue.value.id,
  (newVal: number | undefined) => {
    if (newVal && isAuthenticatedUser.value) {
      loadDockAppointment(newVal);
    }
  },
  { immediate: true },
);
</script>

<style>
:root {
  --location-card-top-row-cell-padding-top: var(--sp-3);
  --location-card-top-row-cell-padding-right: var(--sp-6);
  --location-card-top-row-cell-padding-bottom: var(--sp-3);
  --location-card-top-row-cell-padding-left: var(--sp-6);
}

.location-card {
  --stop-type-default-color: var(--p-gray-300);
  --stop-type-gradient: linear-gradient(
      var(--stop-type-accent-color),
      var(--stop-type-default-color) 60%
    )
    1;

  container: location-card / inline-size;

  .label-with-icon {
    display: flex;
    gap: var(--sp-2);
    align-items: center;
  }

  &.expandable-card {
    border-left: 4px solid var(--stop-type-default-color);

    &.expanded {
      & > .p-card-body {
        .expandable-card-body {
          padding-bottom: 0;
        }
      }
    }

    &.in-progress {
      border-image: var(--stop-type-gradient);
    }

    &.complete {
      border-left-color: var(--stop-type-accent-color);
    }

    &.showing-edit-button {
      --p-text-muted-color: var(--p-surface-400);
    }

    & > .p-card-body {
      padding: 0;

      .expandable-card-body {
        border-bottom: var(--divider);
        padding-right: var(--sp-3);

        & > .rygen-button.expand-button {
          background-color: white;
        }

        .top-row {
          align-items: stretch;

          &.showing-edit-button {
            .edit-button-cell {
              flex: 0 1 auto;
              display: flex;
              justify-content: center;
              align-items: center;
              border-right: none;
              padding-inline: var(--sp-3) 0;

              .edit-button {
                color: var(--p-text-muted-color);
                border-radius: 50%;

                &:hover {
                  color: var(--p-text-color);
                  background: var(--p-content-hover-background);
                }
              }
            }
          }

          .cell-group {
            display: contents;
          }

          .top-row-cell {
            flex: 1 0 auto;
            padding: var(--location-card-top-row-cell-padding-top)
              var(--location-card-top-row-cell-padding-right)
              var(--location-card-top-row-cell-padding-bottom)
              var(--location-card-top-row-cell-padding-left);
            /* important to override KeyValuePair !important */
            height: auto !important;
            border-right: var(--divider);
            flex-grow: 1;
            max-width: 25%;

            &.location-name-cell {
              .key-value-pair-label {
                font-size: 14px;
                font-weight: 600;
              }
            }

            &.address-cell {
              position: relative;

              .named-address {
                display: flex;
                flex-flow: column;
                gap: var(--sp-1);
                max-width: 200px;
              }

              &.editable {
                cursor: pointer;
              }

              &:hover {
                .edit-icon {
                  opacity: 1;
                }
              }

              .edit-icon {
                position: absolute;
                top: var(--sp-3);
                right: var(--sp-3);
                opacity: 0;
                transition: opacity 100ms ease-in;
              }
            }

            &.primary-appt {
              .popup-inputgroup-edit-icon {
                right: calc(var(--sp-3) * -1);
              }
            }
          }
        }
      }

      .expandable-card-expansion {
        border-top: none;
        padding-top: 0;
        display: flex;

        .expansion-left {
          display: flex;
          flex-flow: column;
          width: 50%;

          .location-card-appointments {
            padding: var(--sp-6);

            .location-card-appointments-header {
              display: flex;
              flex-direction: row;
              gap: var(--sp-3);
              width: 100%;

              .dock-appointment-status-chip {
                margin-bottom: var(--sp-3);
              }

              .conduit-logo {
                height: 1rem;
                width: auto;
                margin-left: auto;
              }
            }

            .location-card-appointments-content {
              display: flex;
              flex-direction: column;
              row-gap: var(--sp-6);

              .location-card-appointment-row {
                display: flex;
                gap: var(--sp-3);

                .appointment-cell {
                  flex: 1 1 50%;

                  .popup-inputgroup-edit-icon {
                    right: 0;
                  }
                }
              }
            }
          }

          .accessorials {
            border-top: var(--divider);
            padding: var(--sp-6);

            .input-wrapper {
              margin-top: var(--sp-3);
            }

            .add-button {
              margin-top: var(--sp-3);
            }
          }
        }

        .expansion-right {
          width: 50%;
        }
      }

      .expandable-card-secondary-content {
        margin: 0;
        padding-top: 0;

        .popup-textarea {
          padding: var(--sp-3) var(--sp-6);

          .popup-inputgroup-edit-icon {
            top: var(--sp-3);
            right: var(--sp-3);
          }

          .popup-inputgroup-card {
            top: calc(var(--sp-1) * -1);
            left: var(--sp-2);
            right: var(--sp-2);
            width: initial;
          }

          .popup-inputgroup-placeholder {
            .key-value-pair {
              .key-value-pair-value {
                white-space: initial;
              }
            }
          }
        }
      }

      .key-value-pair {
        .key-value-pair-label {
          font-size: 14px;
          text-transform: none !important;
        }

        .key-value-pair-value {
          display: flex;
          flex-flow: column;
          gap: var(--sp-1);
        }
      }

      .popup-inputgroup {
        min-width: initial;
        width: 100%;

        &.popup-textarea {
          .popup-inputgroup-card {
            top: calc(var(--sp-1) * -1);
            left: var(--sp-2);
          }
        }

        & > .popup-inputgroup-card {
          width: max-content;

          .input-wrapper {
            label {
              font-size: 14px;
              text-transform: none !important;
            }
          }

          /* Revert ^ this ^ style for the date range popup labels */
          .popup-date-range-fields {
            .input-wrapper {
              label {
                font-size: 10px;
                text-transform: uppercase !important;
              }
            }
          }
        }
      }
    }
  }
}

@container location-card (max-width: 800px) {
  .location-card {
    .p-card-body {
      .p-card-content {
        .expandable-card-body {
          .top-row {
            .cell-group {
              display: flex;
              flex: 1 0 40%;
              justify-content: space-between;
              background-color: var(--p-gray-50);

              .top-row-cell {
                &:first-of-type {
                  width: min-content;
                  padding: var(--sp-3) 0 var(--sp-3) var(--sp-6);
                  border: none;

                  .key-value-pair-label {
                    display: none;
                  }

                  .key-value-pair-value {
                    font-size: 14px;
                    font-weight: 600;
                  }
                }

                &.address-cell {
                  max-width: initial;
                  flex: 0 1;
                }
              }
            }

            .top-row-cell {
              max-width: initial;

              &.line-item-summary {
                flex: 1 0 25%;
              }

              &.primary-appt {
                flex: 1 0 35%;
              }
            }
          }
        }

        .expandable-card-expansion {
          .expansion-left {
            width: 100%;

            .location-card-appointments {
              flex-grow: 1;

              .location-card-appointments-content {
                height: 100%;
                justify-content: space-between;
                /*
                This shouldn't be necessary because .location-card-appointments
                already has padding, but the appointments keep overflowing it
                */
                padding-bottom: var(--sp-6);
              }
            }
          }

          .expansion-right {
            flex-flow: column;
            width: 30%;

            .shipping-point-details,
            .contact-details {
              width: 100%;
            }

            .contact-details {
              border-top: var(--divider);
              border-left: none;
            }
          }
        }
      }
    }
  }
}
</style>
